/**
 * btk-addons.js
 * Addon selection, scope modal, basket chip rendering, upgrades banner,
 * and range-change transfer logic.
 *
 * Depends on:
 *   window.BTKAddonsData  - serialised by btk-cart-advanced.liquid
 *   BTKBasketAPI          - btk-basket-api.js
 *
 * Exposes:
 *   window.BTKAddons (public API at bottom of file)
 */
 
window.BTKAddons = (function () {
  'use strict';
 
  // ─── Money helper ──────────────────────────────────────────────────────────
 
  function formatMoney(pence) {
    const pounds = pence / 100;
    return '£' + (Number.isInteger(pounds) ? pounds.toFixed(0) : pounds.toFixed(2));
  }
 
  // ─── Addon data lookup ────────────────────────────────────────────────────
 
  /**
   * Find an addon entry from window.BTKAddonsData by its unique metaHandle.
   * metaHandle is the metaobject system handle (e.g. "glass-drawerside-x2").
   */
  function getEntryByMetaHandle(metaHandle) {
    return (window.BTKAddonsData || []).find(a => a.metaHandle === metaHandle) || null;
  }
 
  // ─── Basket item helpers ───────────────────────────────────────────────────
 
  /**
   * Find a basket item by its BTK item ID across all sections.
   * Works whether BTKBasketAPI uses getActiveProject/getAllItems or similar.
   */
  function findBasketItem(itemId) {
    if (typeof BTKBasketAPI === 'undefined') return null;
    const project = BTKBasketAPI.getActiveProject ? BTKBasketAPI.getActiveProject() : null;
    if (!project) return null;
    return BTKBasketAPI.findItemById ? BTKBasketAPI.findItemById(project, itemId) : null;
  }
 
  function getActiveProject() {
    if (typeof BTKBasketAPI === 'undefined') return null;
    return BTKBasketAPI.getActiveProject ? BTKBasketAPI.getActiveProject() : null;
  }
 
  function getAllBasketItems() {
  try {
    var customerId = window.BTKCustomer && window.BTKCustomer.id
      ? 'shopify_' + window.BTKCustomer.id
      : null;
    if (!customerId) return [];
    var key = 'btk_project_' + customerId;
    var raw = localStorage.getItem(key);
    if (!raw) return [];
    var project = JSON.parse(raw);
    var items = Array.isArray(project.items) ? project.items.slice() : [];
    (project.sections || []).forEach(function(s) {
      (s.items || []).forEach(function(i) { items.push(i); });
    });
    return items;
  } catch(e) { return []; }
}
 
  function saveAndSync() {
    const project = getActiveProject();
    if (!project) return;
    if (BTKBasketAPI.saveProject) BTKBasketAPI.saveProject(project);
    if (BTKBasketAPI.syncToShopifyCart) BTKBasketAPI.syncToShopifyCart();
  }
 
  // ─── Unit type extraction ─────────────────────────────────────────────────
 
  /**
   * Extract unit type code from a basket item's SKU.
   * SKU format: STYLECODE-UNITCODE-WIDTH-DEPTH
   * e.g. MODOTC-2DB-600-570 → unitCode = "2DB"
   */
  function unitCodeFromSku(sku) {
    if (!sku) return '';
    const parts = sku.split('-');
    // parts[0] = style code, parts[1] = unit code
    return parts[1] || '';
  }
 
  /**
   * Return all basket items whose unit type code is in the addon's
   * applicableUnitTypes array. Empty applicableUnitTypes = applies to all.
   */
  function getCompatibleBasketItems(addonEntry) {
    const items = getAllBasketItems();
    if (!addonEntry.applicableUnitTypes || addonEntry.applicableUnitTypes.length === 0) {
      return items;
    }
    return items.filter(item => {
      const code = unitCodeFromSku(item.sku);
      return addonEntry.applicableUnitTypes.includes(code);
    });
  }
 
  // ─── Apply / remove on basket items ──────────────────────────────────────
 
  /**
   * Add or replace an addon on a specific basket item.
   * @param {string}  itemId     - BTK item ID (item.id)
   * @param {object}  addonEntry - entry from window.BTKAddonsData
   * @param {string}  scope      - 'item' | 'global'
   */
  function applyAddonToItem(itemId, addonEntry, scope) {
    const item = findBasketItem(itemId);
    if (!item) return;
 
    if (!Array.isArray(item.attributes.addons)) {
      item.attributes.addons = [];
    }
 
    // Remove any existing entry for the same metaHandle (dedup)
    item.attributes.addons = item.attributes.addons.filter(
      a => a.metaHandle !== addonEntry.metaHandle
    );
 
    item.attributes.addons.push({
      metaHandle:   addonEntry.metaHandle,
      handle:       addonEntry.handle,        // shared aggregation key
      displayName:  addonEntry.displayName,
      qtyPerUnit:   addonEntry.qtyPerUnit,
      price:        addonEntry.price,         // pence per single addon unit
      linePrice:    addonEntry.price * addonEntry.qtyPerUnit,
      variantId:    addonEntry.variantId,
      scope:        scope
    });
  }
 
  function removeAddonFromItem(itemId, metaHandle) {
    const item = findBasketItem(itemId);
    if (!item || !Array.isArray(item.attributes.addons)) return;
    item.attributes.addons = item.attributes.addons.filter(a => a.metaHandle !== metaHandle);
  }
 
  // ─── Scope modal ──────────────────────────────────────────────────────────
 
  function removeScopeModal() {
    document.querySelectorAll('.btk-addon-scope-modal').forEach(el => el.remove());
  }
 
  /**
   * Show the scope selection modal for a global-type addon.
   * @param {object}   addonEntry      - entry from window.BTKAddonsData
   * @param {Function} onGlobal        - called with array of compatible basket items
   * @param {Function} onItem          - called with no args
   * @param {Function} onCancel        - called when cancelled
   */
  function showScopeModal(addonEntry, onGlobal, onItem, onCancel) {
    removeScopeModal();
 
    const compatible = getCompatibleBasketItems(addonEntry);
    const matchCount = compatible.length;
 
    // Sum qty across all compatible items (item.quantity * qtyPerUnit)
    const globalUnits = compatible.reduce(
      (sum, item) => sum + (item.quantity || 1) * addonEntry.qtyPerUnit, 0
    );
    const globalTotal  = formatMoney(addonEntry.price * globalUnits);
    const itemTotal    = formatMoney(addonEntry.price * addonEntry.qtyPerUnit);
 
    const modal = document.createElement('div');
    modal.className = 'btk-addon-scope-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-labelledby', 'btk-scope-title');
 
    modal.innerHTML = `
      <div class="btk-addon-scope-modal__backdrop"></div>
      <div class="btk-addon-scope-modal__box">
        <p class="btk-addon-scope-modal__title" id="btk-scope-title">
          Apply ${addonEntry.displayName}
        </p>
        <p class="btk-addon-scope-modal__sub">Where would you like to apply this upgrade?</p>
        <div class="btk-addon-scope-modal__options">
          <button class="btk-addon-scope-modal__opt btk-addon-scope-modal__opt--global" data-action="global">
            <span class="btk-addon-scope-modal__opt-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <rect x="1" y="3" width="6" height="10" rx="1" fill="currentColor" opacity=".4"/>
                <rect x="9" y="3" width="6" height="10" rx="1" fill="currentColor"/>
              </svg>
            </span>
            <span class="btk-addon-scope-modal__opt-body">
              <span class="btk-addon-scope-modal__opt-label">All matching units</span>
              <span class="btk-addon-scope-modal__opt-detail">
                ${matchCount} unit${matchCount !== 1 ? 's' : ''} &middot; ${globalTotal}
              </span>
            </span>
          </button>
          <button class="btk-addon-scope-modal__opt btk-addon-scope-modal__opt--item" data-action="item">
            <span class="btk-addon-scope-modal__opt-icon">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <rect x="5" y="3" width="6" height="10" rx="1" fill="currentColor"/>
              </svg>
            </span>
            <span class="btk-addon-scope-modal__opt-body">
              <span class="btk-addon-scope-modal__opt-label">Just this unit</span>
              <span class="btk-addon-scope-modal__opt-detail">${itemTotal}</span>
            </span>
          </button>
        </div>
        <button class="btk-addon-scope-modal__cancel" type="button">Cancel</button>
      </div>
    `;
 
    modal.querySelector('[data-action="global"]').addEventListener('click', () => {
      removeScopeModal();
      onGlobal(compatible);
    });
    modal.querySelector('[data-action="item"]').addEventListener('click', () => {
      removeScopeModal();
      onItem();
    });
    modal.querySelector('.btk-addon-scope-modal__cancel').addEventListener('click', () => {
      removeScopeModal();
      if (onCancel) onCancel();
    });
    modal.querySelector('.btk-addon-scope-modal__backdrop').addEventListener('click', () => {
      removeScopeModal();
      if (onCancel) onCancel();
    });
 
    document.body.appendChild(modal);
    requestAnimationFrame(() => modal.classList.add('btk-addon-scope-modal--visible'));
  }
 
  // ─── Card addon selector ──────────────────────────────────────────────────
 
  /**
   * Bind addon toggle behaviour to a product card element.
   *
   * The card must have:
   *   data-btk-card            - present (marks it as a bindable card)
   *   data-btk-item-id         - set AFTER item is added to basket
   *                              (absent / empty when item not yet in basket)
   *
   * Each toggle input must have:
   *   data-meta-handle         - the addon metaobject system handle
   *   class="btk-addon-toggle" - marks it as an addon toggle
   */
  function bindCard(cardEl) {
    const toggles = cardEl.querySelectorAll('.btk-addon-toggle');
    toggles.forEach(toggle => {
      toggle.addEventListener('change', function () {
        const metaHandle = this.dataset.metaHandle;
        const addonEntry = getEntryByMetaHandle(metaHandle);
        if (!addonEntry) return;
 
        const isChecked = this.checked;
        const itemId = cardEl.dataset.btkItemId || null;
 
        if (!isChecked) {
          // ── Deselect ──────────────────────────────────────────────────────
          _removeCardSelection(cardEl, metaHandle);
          if (itemId) {
            removeAddonFromItem(itemId, metaHandle);
            saveAndSync();
            refreshBasketAddonChips();
            renderBanner();
          }
          _updateCardAddonSummary(cardEl);
          return;
        }
 
        // ── Select ────────────────────────────────────────────────────────
        const isGlobalType = addonEntry.type === 'global';
        const compatible   = getCompatibleBasketItems(addonEntry);
 
        // Compatible items EXCLUDING the current card's item (if in basket)
        const others = compatible.filter(i => i.id !== itemId);
        const showModal = isGlobalType && others.length > 0;
 
        // Always apply as item scope from card - global apply is in the basket
_addCardSelection(cardEl, addonEntry, 'item');
if (itemId) {
  applyAddonToItem(itemId, addonEntry, 'item');
  saveAndSync();
  refreshBasketAddonChips();
  renderBanner();
}
_updateCardAddonSummary(cardEl);
      });
    });
  }
 
  // Card selection state (held in data attribute until item is in basket)
 
  function _addCardSelection(cardEl, addonEntry, scope) {
    const sels = _getCardSelections(cardEl).filter(s => s.metaHandle !== addonEntry.metaHandle);
    sels.push({ metaHandle: addonEntry.metaHandle, handle: addonEntry.handle, scope });
    cardEl.dataset.btkAddonSelections = JSON.stringify(sels);
  }
 
  function _removeCardSelection(cardEl, metaHandle) {
    const sels = _getCardSelections(cardEl).filter(s => s.metaHandle !== metaHandle);
    cardEl.dataset.btkAddonSelections = JSON.stringify(sels);
  }
 
  function _getCardSelections(cardEl) {
    try { return JSON.parse(cardEl.dataset.btkAddonSelections || '[]'); } catch { return []; }
  }
 
  /**
   * Update the small "upgrades" price summary line on the card.
   * Targets .btk-addon-summary inside the card.
   */
  function _updateCardAddonSummary(cardEl) {
    const summaryEl = cardEl.querySelector('.btk-addon-summary');
    if (!summaryEl) return;
    const sels  = _getCardSelections(cardEl);
    let   total = 0;
    sels.forEach(sel => {
      const entry = getEntryByMetaHandle(sel.metaHandle);
      if (entry) total += entry.price * entry.qtyPerUnit;
    });
    if (total > 0) {
      summaryEl.textContent = '+ ' + formatMoney(total) + ' in upgrades';
      summaryEl.removeAttribute('hidden');
    } else {
      summaryEl.setAttribute('hidden', '');
    }
  }
 
  /**
   * Called by the add-to-basket handler AFTER a new item is created.
   * Returns the addons array to merge into item.attributes.addons.
   */
  function getCardAddonAttributes(cardEl) {
    const sels = _getCardSelections(cardEl);
    return sels.map(sel => {
      const entry = getEntryByMetaHandle(sel.metaHandle);
      if (!entry) return null;
      return {
        metaHandle:  entry.metaHandle,
        handle:      entry.handle,
        displayName: entry.displayName,
        qtyPerUnit:  entry.qtyPerUnit,
        price:       entry.price,
        linePrice:   entry.price * entry.qtyPerUnit,
        variantId:   entry.variantId,
        scope:       sel.scope
      };
    }).filter(Boolean);
  }
 
  // ─── Basket item chip rendering ───────────────────────────────────────────
 
  /**
   * Re-render all addon chip containers currently in the DOM.
   * Containers are identified by [data-btk-addon-chips] inside
   * a parent element with [data-btk-item-id].
   */
  function refreshBasketAddonChips() {
    document.querySelectorAll('[data-btk-addon-chips]').forEach(container => {
      const itemEl = container.closest('[data-btk-item-id]');
      if (!itemEl) return;
      const itemId = itemEl.dataset.btkItemId;
      const item   = findBasketItem(itemId);
      if (!item) return;
      _renderChipsInto(container, item);
    });
  }
 
  function _renderChipsInto(container, item) {
    container.innerHTML = '';
    const addons = item.attributes?.addons || [];
    if (addons.length === 0) return;
 
    addons.forEach(addon => {
      const chip = document.createElement('div');
      chip.className = 'btk-addon-chip';
      chip.dataset.metaHandle = addon.metaHandle;
      chip.innerHTML = `
        <span class="btk-addon-chip__name">
          ${addon.displayName}${addon.qtyPerUnit > 1 ? ' <span class="btk-addon-chip__qty">×' + addon.qtyPerUnit + '</span>' : ''}
        </span>
        ${addon.scope === 'global'
          ? '<span class="btk-addon-chip__badge" title="Applied to all matching units">All units</span>'
          : ''}
        <span class="btk-addon-chip__price">${formatMoney(addon.linePrice)}</span>
        <button
          class="btk-addon-chip__remove"
          type="button"
          aria-label="Remove ${addon.displayName}"
          data-meta-handle="${addon.metaHandle}"
          data-item-id="${item.id}"
        >&times;</button>
      `;
      chip.querySelector('.btk-addon-chip__remove').addEventListener('click', function () {
        removeAddonFromItem(this.dataset.itemId, this.dataset.metaHandle);
        saveAndSync();
        refreshBasketAddonChips();
        renderBanner();
      });
      container.appendChild(chip);
    });
  }
 
  // ─── Upgrades banner ──────────────────────────────────────────────────────
 
  /**
   * Render (or hide) the upgrades banner.
   * Target: .btk-addon-banner - already in the DOM (rendered by Liquid),
   * hidden by default, shown when addons are present.
   */
  function renderBanner() {
    const banner = document.querySelector('.btk-addon-banner');
    if (!banner) return;
 
    const allItems = getAllBasketItems();
 
    // Aggregate addons by shared handle
    const addonMap = {};
    allItems.forEach(item => {
      const itemQty = item.quantity || 1;
      (item.attributes?.addons || []).forEach(addon => {
        if (!addonMap[addon.handle]) {
          addonMap[addon.handle] = {
            displayName: addon.displayName,
            totalQty:    0,
            totalPrice:  0
          };
        }
        addonMap[addon.handle].totalQty   += addon.qtyPerUnit * itemQty;
        addonMap[addon.handle].totalPrice += addon.linePrice  * itemQty;
      });
    });
 
    const entries = Object.values(addonMap);
 
    if (entries.length === 0) {
      banner.hidden = true;
      return;
    }
 
    const grandTotal = entries.reduce((s, e) => s + e.totalPrice, 0);
    banner.hidden = false;
 
    const rowsHtml = entries.map(e => `
      <div class="btk-addon-banner__row">
        <span class="btk-addon-banner__row-name">${e.displayName}</span>
        <span class="btk-addon-banner__row-qty">&times;${e.totalQty}</span>
        <span class="btk-addon-banner__row-dots"></span>
        <span class="btk-addon-banner__row-price">${formatMoney(e.totalPrice)}</span>
      </div>
    `).join('');
 
    banner.innerHTML = `
      <div class="btk-addon-banner__inner">
        <div class="btk-addon-banner__header">
          <span class="btk-addon-banner__title">Upgrades &amp; Add-ons</span>
          <span class="btk-addon-banner__total">${formatMoney(grandTotal)}</span>
        </div>
        <div class="btk-addon-banner__rows">${rowsHtml}</div>
      </div>
    `;
  }
 
  // ─── Range change transfer ────────────────────────────────────────────────
 
  /**
   * Called by btk-range-change.js after each item is switched to a new style.
   * The unit type code does NOT change during a style switch (SHB stays SHB),
   * so compatibility is re-checked against the same code.
   *
   * Removes any addon whose applicableUnitTypes no longer includes the
   * (unchanged) unit code - edge case for style-specific addons.
   *
   * @param  {object} item           - the already-updated basket item
   * @returns {number}               - count of addons removed (for toast)
   */
  function transferAddonsAfterRangeChange(item) {
    if (!Array.isArray(item.attributes?.addons) || item.attributes.addons.length === 0) return 0;
 
    const unitCode = unitCodeFromSku(item.sku); // sku has already been updated to new style
    let removed = 0;
 
    item.attributes.addons = item.attributes.addons.filter(addon => {
      const entry = getEntryByMetaHandle(addon.metaHandle);
      // If entry not found or has no restriction, keep it
      if (!entry) { removed++; return false; }
      if (!entry.applicableUnitTypes || entry.applicableUnitTypes.length === 0) return true;
      if (!entry.applicableUnitTypes.includes(unitCode)) { removed++; return false; }
      return true;
    });
 
    return removed;
  }
 
  // ─── syncToShopifyCart extension ─────────────────────────────────────────
  //
  // This function produces the addon cart lines to be added AFTER all
  // cabinet lines in syncToShopifyCart().
  //
  // Usage in btk-basket-api.js syncToShopifyCart():
  //
  //   // After adding all cabinet lines:
  //   if (window.BTKAddons) {
  //     const addonLines = window.BTKAddons.buildAddonCartLines(allItems);
  //     for (const line of addonLines) {
  //       await addLineToCart(line.variantId, line.qty, line.properties);
  //     }
  //   }
  //
  // The addon cart lines carry underscore-prefixed properties so Shopify
  // hides them from customer-facing order details.
 
  /**
   * Build the aggregated addon cart lines from all basket items.
   * Returns an array of { variantId, qty, properties } objects.
   */
  function buildAddonCartLines(allItems) {
    const addonMap = {};
 
    allItems.forEach(item => {
      const itemQty = item.quantity || 1;
      (item.attributes?.addons || []).forEach(addon => {
        const key = addon.handle;
        if (!addonMap[key]) {
          addonMap[key] = {
            variantId:   addon.variantId,
            displayName: addon.displayName,
            handle:      addon.handle,
            totalQty:    0,
            unitTitles:  []
          };
        }
        addonMap[key].totalQty  += addon.qtyPerUnit * itemQty;
        addonMap[key].unitTitles.push(item.productTitle || 'Unit');
      });
    });
 
    return Object.values(addonMap)
      .filter(a => a.variantId && a.totalQty > 0)
      .map(a => ({
        variantId:  a.variantId,
        qty:        a.totalQty,
        properties: {
          '_btk_addon':       a.handle,
          '_btk_addon_units': a.unitTitles.join(', ')
        }
      }));
  }
 
  // ─── Initialise ───────────────────────────────────────────────────────────
 
  function init() {
    // Bind all product cards present on page load
    document.querySelectorAll('[data-btk-card]').forEach(bindCard);
 
    // Basket page - render banner and chips on load
    renderBanner();
    refreshBasketAddonChips();
  }
 
  // ─── Public API ───────────────────────────────────────────────────────────
 
  return {
    init,
    bindCard,
    applyAddonToItem,
    removeAddonFromItem,
    getCardAddonAttributes,
    getCompatibleBasketItems,
    refreshBasketAddonChips,
    renderBanner,
    buildAddonCartLines,
    transferAddonsAfterRangeChange
  };
 
})();